import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { AccountBalance, accountBalanceAtom, AccountBalanceMaybe, accountsAtom } from '../../state';
import { CashInfo, CashInfoByCurrency, CashInfoByCurrencyMaybe, Currency, SelectedAccountOrClient } from '../../types';
import { isDefined, Loadable, LoadableState, mergeNumericValues, ObjectSetLoadable } from '../../utils';

const isAccountBalanceLoaded = (item: AccountBalanceMaybe): item is AccountBalance => {
  return !item.isLoading;
};

export const useAccountBalanceCommon = () => {
  const [accountBalance, setAccountBalance] = useAtom(accountBalanceAtom);
  const accounts = useAtomValue(accountsAtom);

  const getAccountBalance = useCallback(
    (accountNumber: string): AccountBalanceMaybe => {
      if (!accounts.isLoaded) {
        return Loadable.loading;
      }

      if (accountBalance.isLoading || !accountBalance.hasById(accountNumber)) {
        // if not accountBalance is set yet, we create an empty one
        const targetAccountNumber = accounts.clients
          .map(client => client.accounts)
          .flat()
          .find(account => account.accountNumber === accountNumber);
        if (!targetAccountNumber) {
          throw new Error(`Account not found: ${accountNumber}`);
        }

        return accountBalance.isLoading
          ? {
              ...targetAccountNumber,
              isLoading: true,
            }
          : {
              ...targetAccountNumber,
              cashInfo: {},
              investmentLoan: 0,
              isLoading: false,
            };
      }

      const res = accountBalance.get(accountNumber);
      if (!res) {
        throw new Error(`Account has not been set up for accountBalance: ${accountNumber}`);
      }
      return res;
    },
    [accountBalance, accounts],
  );

  const updateAccountBalance = useCallback(
    (accountBalance: AccountBalance | ObjectSetLoadable<AccountBalance>) => {
      if (accountBalance instanceof ObjectSetLoadable) {
        setAccountBalance(accountBalance);
      } else {
        setAccountBalance(prev => {
          return prev.copy().overwrite(accountBalance);
        });
      }
    },
    [setAccountBalance],
  );

  const doesClientHaveCashInventory = useMemo(() => {
    if (!accountBalance.isLoading) {
      return accountBalance
        .values()
        .some(acc => isDefined(acc.cashInfo) && Object.values(acc.cashInfo).some(c => c.inventoryHuf > 0));
    }

    return false;
  }, [accountBalance]);

  const getCashInfoByCurrency = useCallback(
    (accountOrClient: SelectedAccountOrClient): CashInfoByCurrencyMaybe => {
      if (accountOrClient.account) {
        const accountBalance = getAccountBalance(accountOrClient.account.accountNumber);
        if (accountBalance.isLoading) {
          return Loadable.loading;
        }
        return Loadable.loaded({
          cashInfo: accountBalance.cashInfo,
        } as CashInfoByCurrency);
      } else {
        const accountBalanceList = accountOrClient.accounts.map(account => getAccountBalance(account.accountNumber));

        if (accountBalanceList.some(_ => _.isLoading)) {
          return Loadable.loading;
        }

        const cashInfoByCurrency = accountBalanceList
          .filter(isAccountBalanceLoaded)
          .map(_ => _.cashInfo)
          .reduce(
            (curr: CashInfoByCurrency, next) => {
              if (next && curr) {
                (Object.keys(next) as Array<Currency>).forEach(key => {
                  if (curr.cashInfo[key]) {
                    curr.cashInfo[key] = mergeNumericValues([curr.cashInfo[key], next[key]]);
                  } else {
                    curr.cashInfo[key] = next[key];
                  }
                });
              }

              return curr;
            },
            { cashInfo: {} } as CashInfoByCurrency,
          );

        return Loadable.loaded(cashInfoByCurrency);
      }
    },
    [getAccountBalance],
  );

  const getCashInfoForCurrency = useCallback(
    (accountOrClient: SelectedAccountOrClient, currency: Currency): LoadableState<CashInfo> => {
      const cashInfos = getCashInfoByCurrency(accountOrClient);
      if (!cashInfos.isLoading) {
        return Loadable.loaded((cashInfos as CashInfoByCurrency).cashInfo[currency]);
      } else {
        return Loadable.loading;
      }
    },
    [getCashInfoByCurrency],
  );

  const getInvestmentLoan = useCallback(
    (accountOrClient: SelectedAccountOrClient): LoadableState<number> => {
      if (accountOrClient.account) {
        const accountBalance = getAccountBalance(accountOrClient.account.accountNumber);
        if (accountBalance.isLoading) {
          return Loadable.loading;
        }
        return Loadable.loaded(accountBalance.investmentLoan);
      } else {
        const res = accountOrClient.accounts
          .map(account => getAccountBalance(account.accountNumber))
          .filter(isAccountBalanceLoaded)
          .map(_ => _.investmentLoan)
          .reduce((acc, curr) => acc + curr, 0);
        return Loadable.loaded(res);
      }
    },
    [getAccountBalance],
  );

  return useMemo(
    () => ({
      accountBalance,
      doesClientHaveCashInventory,
      updateAccountBalance,
      getAccountBalance,
      getInvestmentLoan,
      getCashInfoByCurrency,
      getCashInfoForCurrency,
    }),
    [
      accountBalance,
      doesClientHaveCashInventory,
      updateAccountBalance,
      getAccountBalance,
      getInvestmentLoan,
      getCashInfoByCurrency,
      getCashInfoForCurrency,
    ],
  );
};
